import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp } from "lucide-react";
import { portfolioCompanies } from "@/data/portfolioCompanies";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import Navigation from "@/components/Navigation";
import StructuredData from "@/components/StructuredData";

const Portfolio = () => {
  const [selectedStage, setSelectedStage] = useState<string>("All");
  
  useEffect(() => {
    document.title = "Portfolio | AMX Ventures — AI Infrastructure, Sovereign & Physical AI";
    const desc = "AMX Ventures portfolio: Onepane, Benev, Rushot, Nimo Infinity, RAIGen, and Sirocco Ventures. We invest, then work inside each company as Investor & Operating Partner.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", "https://amx.ventures/portfolio");
  }, []);
  
  const stages = ["All", ...Array.from(new Set(portfolioCompanies.map(c => c.stage)))];
  const filteredCompanies = selectedStage === "All" 
    ? portfolioCompanies 
    : portfolioCompanies.filter(c => c.stage === selectedStage);

  const portfolioSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "AMX Ventures Portfolio Companies",
    "description": "B2B SaaS companies backed by AMX Ventures, scaling through ecosystem-led growth.",
    "itemListElement": portfolioCompanies.map((company, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Organization",
        "name": company.name,
        "description": company.description,
        "url": `https://amxventures.com/portfolio/${company.id}`,
        "logo": company.logo
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <StructuredData data={portfolioSchema} />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="py-20 px-4 border-b-2 bg-secondary">
          <div className="container mx-auto max-w-6xl">
            <div className="space-y-8">
              <div>
                <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-4 text-foreground">
                  Our Portfolio
                </h1>
                <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl">
                  Companies we work inside, not just fund. Every AMX investment comes with capital, market access, and an operating partner in the room.
                </p>
              </div>

              <div className="bg-background border-l-4 border-accent p-8 space-y-4">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  I&apos;ve spent 21 years founding, scaling, and exiting enterprise software companies, from building and exiting Adfolks, Actualize, and AppsIntegra to investing in Onepane, Benev, Rushot, Nimo Infinity, RAIGen, and Sirocco Ventures. That experience shaped how AMX Ventures operates today: we invest in AI infrastructure, sovereign &amp; physical AI, then work alongside founders on the deals, co-sell motions, and market access that win enterprise customers.
                </p>
              </div>

              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-3">
                {stages.map((stage) => (
                  <Button
                    key={stage}
                    onClick={() => setSelectedStage(stage)}
                    variant={selectedStage === stage ? "default" : "outline"}
                    className={selectedStage === stage 
                      ? "bg-accent text-accent-foreground hover:bg-accent/90 font-bold border-2 border-accent" 
                      : "border-2 border-accent text-accent hover:bg-accent hover:text-accent-foreground font-bold"
                    }
                  >
                    {stage}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio Grid */}
        <section className="py-20 px-4 bg-background">
          <div className="container mx-auto max-w-7xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCompanies.map((company) => (
                <Link key={company.id} to={`/portfolio/${company.id}`}>
                  <Card className="h-full group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-accent overflow-hidden">
                    <CardHeader>
                      {/* Logo Container */}
                      <div className="relative mb-6 -mt-6 -mx-6 px-6 pt-8 pb-6 bg-gradient-to-br from-secondary via-background to-secondary border-b-2 border-border">
                        <div className="flex items-start justify-between">
                          <div className="w-24 h-24 bg-background rounded-2xl flex items-center justify-center border-2 border-border shadow-sm group-hover:shadow-md group-hover:border-accent transition-all duration-300 p-4">
                            {typeof company.logo === 'string' && company.logo.length <= 3 ? (
                              <span className="text-4xl font-black bg-gradient-to-br from-accent via-primary to-accent bg-clip-text text-transparent">
                                {company.logo}
                              </span>
                            ) : (
                              <img 
                                src={company.logo} 
                                alt={`${company.name} logo`} 
                                className="w-full h-full object-contain filter group-hover:brightness-110 transition-all duration-300" 
                              />
                            )}
                          </div>
                          <Badge variant="secondary" className="text-xs font-bold border border-border">
                            {company.stage}
                          </Badge>
                        </div>
                      </div>
                      <CardTitle className="text-2xl mb-2 group-hover:text-accent transition-colors font-black">
                        {company.name}
                      </CardTitle>
                      <CardDescription className="text-base font-medium text-muted-foreground">
                        {company.tagline}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {company.description}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <Badge variant="outline" className="text-xs font-bold">
                          {company.category}
                        </Badge>
                        <div className="flex items-center text-accent text-sm font-bold group-hover:translate-x-1 transition-transform">
                          Learn more
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 border-t-2 border-border bg-secondary">
          <div className="container mx-auto max-w-4xl text-center">
            <TrendingUp className="w-16 h-16 text-accent mx-auto mb-6" />
            <h2 className="text-4xl font-black mb-4 text-foreground">Want AMX in your corner?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              AMX Ventures backs founders building AI infrastructure, sovereign &amp; physical AI, then puts them in front of buyers through hyperscaler co-sell and enterprise access. If that&apos;s you, let&apos;s talk.
            </p>
            <Button size="lg" className="hover:scale-105 transition-transform font-bold bg-accent text-accent-foreground hover:bg-accent/90" asChild>
              <Link to="/schedule">
                Schedule a Meeting
                <ArrowRight className="ml-2" />
              </Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Portfolio;
